import React from "react";

import type { ChildProps as Props } from "./EmbeddedContent";

// This needs to be a string so that webpack doesn't interfere. Some webpack objects may
// not exist at the time at which this runs (on initial render, before the react app starts
// in an ssr app, and before the entirety of the dom has been created)
const shadowRootPolyfill = `
  const attachShadowRoots = (root) => {
    root.querySelectorAll('template[shadowroot]').forEach((template) => {
      const mode = template.getAttribute('shadowroot');
      const shadowRoot = template.parentNode.attachShadow({ mode });
      shadowRoot.appendChild(template.content);
      template.remove();
      attachShadowRoots(shadowRoot);
    });
  }; 
  attachShadowRoots(document);
`;

export const ShadowEmbedder = ({ content }: Props): JSX.Element | null => {
  // It's important to use layout effect here so that this declarative shadow
  // dom polyfill runs after the dom node exists, but before the browser paints
  // the node to the screen. This will prevent a flicker/layout shift for non
  // ssr'd pages. You'll see that this polyfill is also run in a script tag, but
  // this actually only runs if the content is ssr'd (since if it isn't, react
  // will ignore script tags within a "dangerouslySetInnerHTML")
  React.useLayoutEffect(() => {
    // eslint-disable-next-line no-eval
    eval(shadowRootPolyfill);
  }, []);

  return (
    <div
      dangerouslySetInnerHTML={{
        // We want to encapsulate everything that is mutated in "dangerouslySetInnerHTML" so that react
        // doesn't pick up/mess with the mutations we make to these elements.
        __html: `
        <div>
          ${
            ""
            // We use the declarative shadow dom here so that serverside rendering creates the element
            // on its render. This makes things better for seo, as search engines can see the content
            // of the page without running javascript, and google actually does use this content like
            // it would any other content on the page. Read more about declarative shadow dom here:
            // https://web.dev/declarative-shadow-dom/
          }
          <template shadowroot='open'>
          ${content}
          </template>
        </div>
        <script>${
          ""
          // POLYFILL FOR BROWSERS THAT DON'T SUPPORT DECLARATIVE SHADOW DOM
          // This Polyfill is run directly after the declarative shadowdom element is created. This is
          // so that the browser finishes placing it on the page and actually renders it, before it
          // continues through the dom and so the page loads in order from top to bottom (like it
          // usually would)

          // This script is run *here* to make sure that the serverside rendered template gets the polyfill
          // applied immediately after it is created, and that it doesn't need to wait for react to
          // rehydrate before it runs. "dangerouslySetInnerHTML" doesn't actually run scripts so this
          // script will only run on the inital dom creation and be ignored when re-hydration happens
          // or when the initial react render happens on the front end in useLayoutEffect (for non-ssr'd apps).

          // Running this polyfill multiple times also won't do anything un-intended since it is an
          // idempotent function. Since React ignores diffing for elements within "dangerouslySetInnerHTML",
          // React won't interfere with mutations that we do on elements as long as they are all within
          // "dangerouslySetInnerHTML". This is why the div we attach the shadow root to, the template, and
          // the script that modifies it, are all encapsulated in "dangerouslySetInnerHTML".
        }
          (() => {
            ${shadowRootPolyfill}
          })()
        </script>
        `,
      }}
    />
  );
};
